/**
 * PlanUpgradeModal
 * Modal for upgrading to Pro plan. Shows plan selection (monthly/annual),
 * creates subscription via API, handles redirect or Stripe payment.
 *
 * @see BILLING_IMPLEMENTATION_GUIDE.md
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  useBilling,
  useBillingPlans,
  useBillingCards,
  useCreateSubscription,
  useBillingRedirect,
  useSetDefaultCard,
} from "../../../hooks/useBilling";
import { useBillingStore } from "../../../stores/billingStore";
import { useTheme } from "@finitless/shared/stores";
import { useToastContext } from "@finitless/shared/contexts";
import { appConfig } from "@finitless/shared/config";
import Modal from "../UIElements/Modal";
import { Button } from "../UIElements/Button";
import { Icon } from "../UIElements/Icon";
import { Skeleton } from "../Skeletons";
import SavedCardsList from "./SavedCardsList";
import AddCardForm from "./AddCardForm";

/** Load Stripe only when payment step is reached (lazy load to avoid loading SDK for all users) */
const getStripePromise = () => {
  if (!appConfig?.stripe_public_key) return null;
  return import("@stripe/stripe-js").then(({ loadStripe }) =>
    loadStripe(appConfig.stripe_public_key),
  );
};

const formatPrice = (amount) => {
  if (amount == null) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const PlanUpgradeCheckoutForm = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation(["billing"]);
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToastContext();
  const [isConfirming, setIsConfirming] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsConfirming(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + window.location.pathname,
        },
        redirect: "if_required",
      });
      if (error) {
        toast.error(error?.message || t("paymentFailed"));
        return;
      }
      const status = paymentIntent?.status;
      if (status === "succeeded") {
        onSuccess?.();
        return;
      }
      if (status === "processing") {
        toast.success(t("paymentProcessing"));
        onSuccess?.();
        return;
      }
      // requires_action, requires_payment_method, requires_confirmation, canceled, or unknown
      toast.error(
        status === "requires_payment_method"
          ? t("paymentFailed")
          : t("paymentUnexpectedStatus"),
      );
    } catch (err) {
      toast.error(err?.message || t("paymentFailed"));
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          onClick={onCancel}
          emphasis="outline"
          intent="neutral"
          size="touch"
          className="min-w-0 flex-1 border px-4 py-2 whitespace-normal"
        >
          {t("cancel")}
        </Button>
        <Button
          type="submit"
          disabled={!stripe || isConfirming}
          emphasis="solid"
          intent="brand"
          size="touch"
          className="min-w-0 flex-1 px-4 py-2 whitespace-normal"
        >
          {isConfirming ? t("paying") : t("pay")}
        </Button>
      </div>
    </form>
  );
};

const PlanUpgradeModal = () => {
  const { t } = useTranslation(["billing"]);
  const toast = useToastContext();
  const theme = useTheme();
  const { showUpgradeModal, setShowUpgradeModal } = useBillingStore();
  const { upgradePlanId, refetch } = useBilling();
  const { data: plans = [], isLoading: plansLoading } =
    useBillingPlans(showUpgradeModal);
  const createSubscription = useCreateSubscription();
  const setDefaultCard = useSetDefaultCard();
  const { redirectToBillingPortal } = useBillingRedirect();
  const { data: cards = [], isLoading: cardsLoading } =
    useBillingCards(showUpgradeModal);

  const [period, setPeriod] = useState("monthly");
  const [clientSecret, setClientSecret] = useState(null);
  const [step, setStep] = useState("plan"); // plan | payment
  const [stripePromise, setStripePromise] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const stripePromiseRef = useRef(null);
  const isDismissedRef = useRef(false);

  // Auto-select default card when cards load
  useEffect(() => {
    if (cards.length > 0 && !selectedCardId) {
      const defaultCard = cards.find((c) => c.default) ?? cards[0];
      setSelectedCardId(defaultCard?.id ?? null);
    }
  }, [cards, selectedCardId]);

  const upgradePlan = plans.find((p) => p?.id === upgradePlanId);

  // Lazy-load Stripe only when payment step is reached (avoids loading SDK for all users)
  useEffect(() => {
    if (!clientSecret || !appConfig?.stripe_public_key) return;
    if (!stripePromiseRef.current) {
      stripePromiseRef.current = getStripePromise();
    }
    setStripePromise(stripePromiseRef.current);
  }, [clientSecret]);

  useEffect(() => {
    if (showUpgradeModal) {
      isDismissedRef.current = false;
    }
  }, [showUpgradeModal]);

  const handleClose = () => {
    isDismissedRef.current = true;
    setShowUpgradeModal(false);
    setStep("plan");
    setClientSecret(null);
    setSelectedCardId(null);
    setShowAddCard(false);
  };

  const handleUpgradeClick = async () => {
    if (!upgradePlanId) {
      toast.error(t("noUpgradePlanFound"));
      return;
    }

    isDismissedRef.current = false;

    try {
      // Set selected card as default if it differs from current default
      if (selectedCardId) {
        const currentDefault = cards.find((c) => c.default);
        if (currentDefault?.id !== selectedCardId) {
          await setDefaultCard.mutateAsync({ cardId: selectedCardId });
          if (isDismissedRef.current) return;
        }
      }

      const response = await createSubscription.mutateAsync({
        plan_id: upgradePlanId,
        period,
        user_paymethod_id: selectedCardId,
      });

      if (isDismissedRef.current) return;

      const redirectUrl =
        response?.redirect_url ??
        response?.result?.redirect_url ??
        response?.url;
      const secret =
        response?.client_secret ??
        response?.result?.client_secret ??
        response?.result?.data?.client_secret;

      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }

      if (secret && appConfig?.stripe_public_key) {
        setClientSecret(secret);
        setStep("payment");
        return;
      }

      // API succeeded without redirect/Stripe - refetch and close
      await refetch();
      toast.success(t("planUpdatedSuccessfully"));
      handleClose();
    } catch (err) {
      if (!isDismissedRef.current) {
        toast.error(err?.message || t("paymentFailed"));
      }
    }
  };

  const handleFallbackRedirect = () => {
    handleClose();
    redirectToBillingPortal().catch((err) =>
      toast.error(err?.message || t("redirectFailed")),
    );
  };

  const handleBackToPlan = () => {
    setStep("plan");
    setClientSecret(null);
    setShowAddCard(false);
  };

  const elementsOptions = useMemo(
    () =>
      clientSecret
        ? {
            clientSecret,
            appearance: {
              theme: theme === "dark" ? "night" : "stripe",
              variables: { colorPrimary: "#8a42ff" },
            },
          }
        : undefined,
    [clientSecret, theme],
  );

  if (!showUpgradeModal) return null;

  return (
    <Modal
      isOpen={showUpgradeModal}
      onClose={handleClose}
      title={t("upgradeToPro")}
      subtitle={
        step === "plan"
          ? t("upgradeModalSubtitle")
          : t("upgradeModalPaymentSubtitle")
      }
      maxWidth="max-w-md"
    >
      {step === "plan" ? (
        <div className="space-y-4">
          {plansLoading ? (
            <Skeleton className="h-32 w-full rounded-lg" />
          ) : !upgradePlan ? (
            <div className="border-border bg-warning rounded-lg border p-4">
              <p className="text-foreground text-sm">
                {t("noUpgradePlanFound")}
              </p>
              <Button
                type="button"
                onClick={handleFallbackRedirect}
                emphasis="link"
                intent="brand"
                size="touch"
                className="mt-3 min-w-0 whitespace-normal underline"
              >
                {t("manageBilling")}
              </Button>
            </div>
          ) : (
            <>
              <div className="border-border bg-muted rounded-lg border p-4">
                <h4 className="text-foreground font-semibold">
                  {upgradePlan.name}
                </h4>
                <div className="mt-2 flex gap-2">
                  <Button
                    type="button"
                    onClick={() => setPeriod("monthly")}
                    emphasis="solid"
                    intent={period === "monthly" ? "brand" : "neutral"}
                    size="touch"
                    className="min-w-0 flex-1 whitespace-normal"
                  >
                    {t("monthly")} —{" "}
                    {formatPrice(upgradePlan.prices?.monthly?.amount)}
                  </Button>
                  {upgradePlan.prices?.annual?.amount != null && (
                    <Button
                      type="button"
                      onClick={() => setPeriod("annual")}
                      emphasis="solid"
                      intent={period === "annual" ? "brand" : "neutral"}
                      size="touch"
                      className="min-w-0 flex-1 whitespace-normal"
                    >
                      {t("annual")} —{" "}
                      {formatPrice(upgradePlan.prices?.annual?.amount)}
                    </Button>
                  )}
                </div>
              </div>

              {/* Payment Methods Section */}
              <div className="border-border bg-muted rounded-lg border p-4">
                <div className="mb-3">
                  <span className="text-foreground text-xs font-semibold tracking-wider uppercase">
                    {t("stripe")}
                  </span>
                  <h4 className="text-foreground mt-1 font-semibold">
                    {t("paymentMethods")}
                  </h4>
                </div>
                {showAddCard ? (
                  <AddCardForm
                    onSuccess={(savedCard) => {
                      setShowAddCard(false);
                      if (savedCard?.id) {
                        setSelectedCardId(savedCard.id);
                      }
                    }}
                    onCancel={() => setShowAddCard(false)}
                  />
                ) : (
                  <SavedCardsList
                    cards={cards}
                    selectedCardId={selectedCardId}
                    onSelectCard={(card) => setSelectedCardId(card.id)}
                    isLoading={cardsLoading}
                    onAddCard={() => setShowAddCard(true)}
                  />
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  onClick={handleClose}
                  emphasis="outline"
                  intent="neutral"
                  size="touch"
                  className="min-w-0 flex-1 border px-4 py-2 whitespace-normal"
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="button"
                  onClick={handleUpgradeClick}
                  disabled={
                    createSubscription.isPending ||
                    setDefaultCard.isPending ||
                    showAddCard
                  }
                  emphasis="solid"
                  intent="brand"
                  size="touch"
                  className="min-w-0 flex-1 px-4 py-2 whitespace-normal"
                >
                  {createSubscription.isPending || setDefaultCard.isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <Icon name="Loader" className="h-4 w-4 animate-spin" />
                      {t("paying")}
                    </span>
                  ) : (
                    t("pay")
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      ) : clientSecret && stripePromise ? (
        <Elements stripe={stripePromise} options={elementsOptions}>
          <PlanUpgradeCheckoutForm
            onSuccess={() => {
              refetch();
              toast.success(t("planUpdatedSuccessfully"));
              handleClose();
            }}
            onCancel={handleBackToPlan}
          />
        </Elements>
      ) : clientSecret && appConfig?.stripe_public_key ? (
        <div className="flex items-center justify-center py-8">
          <Icon
            name="Loader"
            className="text-muted-foreground h-8 w-8 animate-spin"
          />
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            {t("redirectingToBilling")}
          </p>
          <Button
            type="button"
            onClick={handleFallbackRedirect}
            emphasis="solid"
            intent="brand"
            size="touch"
            className="w-full min-w-0 px-4 py-2 whitespace-normal"
          >
            {t("continueToBilling")}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default PlanUpgradeModal;
